import { useEffect, useState } from 'react';

import { useSelector } from 'hooks';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import { getStore } from 'utils/reduxStore';

import EventBus from '../../../../../lib/EventBus';
import { showSnackbar } from '../../../../shared';
import { ReportDownloadingWSData } from '../type';
import CollectedReportDrawer from './CollectedReportDrawer';
import { useGeneratedReportsColumnDefinitions } from './hooks/useGeneratedReportsColumnDefinitions';
import { useGetGeneratedReport } from './hooks/useGetGeneratedReport';
import TableCRUDLayout from 'components/shared/CRUD/TableCRUDLayout';

const BasicReports = () => {
  const { t } = useTranslation('common');
  const { data: convertedData, isFetching } = useGetGeneratedReport();
  const columnDefinitions = useGeneratedReportsColumnDefinitions();
  const [containerOpen, setContainerOpen] = useState<boolean>(false);
  const { hasBasicReportAccess } = useSelector(({ grants }) => grants);
  const { userDetails } = getStore().getState();
  const queryClient = useQueryClient();

  const accountId = userDetails.accountId;
  const eventBusAddress = `/reports/${accountId}`;

  const handleClose = () => {
    setContainerOpen(false);
  };

  const unregisterReportsSocket = () => {
    EventBus.unregister(eventBusAddress, onReportStatusUpdate);
  };

  const registerReportsSocket = () => {
    unregisterReportsSocket();
    EventBus.register(eventBusAddress, onReportStatusUpdate);
  };

  const onReportStatusUpdate = async (
    reportData: ReportDownloadingWSData['body']
  ) => {
    const reportKey = `generatedReport.${reportData.reportType}`;
    if (reportData.status === 'started') {
      showSnackbar({
        message: t('generatedReportStarted', { reportType: t(reportKey) }),
        severity: 'info'
      });
    } else if (reportData.status === 'finished') {
      showSnackbar({
        message: t('generatedReportReady', { reportType: t(reportKey) }),
        downloadUrl: reportData.downloadUrl,
        filename: reportData.filename,
        severity: 'success',
        hideCloseButton: true
      });
    } else if (reportData.status === 'failed') {
      showSnackbar({
        message: t('generatedReportFailed', { reportType: t(reportKey) }),
        severity: 'error'
      });
    }

    await queryClient.invalidateQueries({
      queryKey: ['generated']
    });
  };

  useEffect(() => {
    registerReportsSocket();
  }, []);

  return (
    <>
      <CollectedReportDrawer
        containerOpen={containerOpen}
        handleClose={handleClose}
      />
      <TableCRUDLayout
        columns={columnDefinitions}
        shouldRenderToolBar={false}
        renderFormContainer={false}
        editable={false}
        rows={convertedData ?? []}
        loading={isFetching}
        getRowId={(row) => row.fileId}
        onAddClick={() => hasBasicReportAccess && setContainerOpen(true)}
      />
    </>
  );
};

export default BasicReports;
